"use client"

import * as React from "react"
import { LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription,
  UICardAction
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardDefaultProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  title: string
  description?: string
  icon?: LucideIcon
  status?: 'default' | 'pending' | 'in-progress' | 'completed' | 'error'
  priority?: 'low' | 'medium' | 'high'
  primaryAction?: {
    text: string
    onClick: () => void
    variant?: 'default' | 'accent' | 'outline'
    disabled?: boolean
  }
  secondaryAction?: {
    text: string
    onClick: () => void
    variant?: 'outline' | 'ghost'
    disabled?: boolean
  }
  metadata?: Array<{
    label: string
    value: string | number
  }>
  children?: React.ReactNode
}

/**
 * Configurable Default Card Component
 * Versatile card for tasks, content, and general-purpose use cases
 * Based on shadcn/ui Default Card block with Seahorse branding
 */
export const UICardDefault = React.forwardRef<HTMLDivElement, UICardDefaultProps>(
  ({
    title,
    description,
    icon: Icon,
    status = 'default',
    priority,
    primaryAction,
    secondaryAction,
    metadata,
    className,
    children,
    ...props
  }, ref) => {
    const statusStyles = {
      default: "",
      pending: "border-l-4 border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/20",
      'in-progress': "border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-950/20",
      completed: "border-l-4 border-l-green-500 bg-green-50 dark:bg-green-950/20",
      error: "border-l-4 border-l-red-500 bg-red-50 dark:bg-red-950/20"
    }

    const priorityIndicator = {
      low: "h-2 w-2 rounded-full bg-blue-500",
      medium: "h-2 w-2 rounded-full bg-yellow-500",
      high: "h-2 w-2 rounded-full bg-red-500"
    }

    return (
      <UICard
        ref={ref}
        className={cn(
          "relative",
          statusStyles[status],
          className
        )}
        {...props}
      >
        {/* Header */}
        <UICardHeader>
          <UIBox className="flex items-start justify-between">
            <UIBox className="flex items-start gap-3 min-w-0 flex-1">
              {/* Icon */}
              {Icon && (
                <UIBox className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/10 dark:bg-accent/20 mt-0.5">
                  <Icon className="h-5 w-5 text-accent" />
                </UIBox>
              )}

              {/* Title and description */}
              <UIBox className="min-w-0 flex-1">
                <UIBox className="flex items-center gap-2">
                  <UICardTitle>
                    <UITypography variant="h4" size="default" weight="semibold">
                      {title}
                    </UITypography>
                  </UICardTitle>
                  {priority && (
                    <UIBox className={priorityIndicator[priority]} title={`${priority} priority`} />
                  )}
                </UIBox>

                {description && (
                  <UICardDescription className="mt-1">
                    <UITypography variant="p" size="sm" color="muted">
                      {description}
                    </UITypography>
                  </UICardDescription>
                )}
              </UIBox>
            </UIBox>

            {/* Status indicator */}
            {status !== 'default' && (
              <UICardAction>
<UITypography
                  as="span"
                  variant="caption"
                  size="xs"
                  weight="medium"
                  className={cn(
                    "inline-flex items-center rounded-full px-2 py-1",
                    {
                      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200": status === 'pending',
                      "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200": status === 'in-progress',
                      "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200": status === 'completed',
                      "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200": status === 'error',
                    }
                  )}
                >
                  {status.replace('-', ' ')}
                </UITypography>
              </UICardAction>
            )}
          </UIBox>
        </UICardHeader>

        {/* Content */}
        {(metadata || children) && (
          <UICardContent className="py-4">
            {/* Metadata */}
            {metadata && metadata.length > 0 && (
              <UIBox className="grid grid-cols-2 gap-4 text-sm">
                {metadata.map((item, index) => (
                  <UIBox key={index} className="space-y-1">
                    <UITypography variant="p" color="muted" weight="medium">{item.label}</UITypography>
                    <UITypography variant="p" color="default">{item.value}</UITypography>
                  </UIBox>
                ))}
              </UIBox>
            )}

            {/* Custom content */}
            {children}
          </UICardContent>
        )}

        {/* Footer with actions */}
        {(primaryAction || secondaryAction) && (
          <UICardFooter className="pt-4 border-t border-border/50">
            <UIBox className="flex w-full gap-2">
              {primaryAction && (
                <UIButton
                  variant={primaryAction.variant || 'default'}
                  onClick={primaryAction.onClick}
                  disabled={primaryAction.disabled}
                  className="flex-1"
                >
                  {primaryAction.text}
                </UIButton>
              )}
              {secondaryAction && (
                <UIButton
                  variant={secondaryAction.variant || 'outline'}
                  onClick={secondaryAction.onClick}
                  disabled={secondaryAction.disabled}
                  className={primaryAction ? "flex-1" : "w-full"}
                >
                  {secondaryAction.text}
                </UIButton>
              )}
            </UIBox>
          </UICardFooter>
        )}
      </UICard>
    )
  }
)

UICardDefault.displayName = "UICardDefault"